// Shared account filter model + UI: quick-filter chips, select filters and saved views.
// Used by the Accounts table (wide) and the narrow bottom-sheet. The quick-filter predicates
// are also the source of truth for the Accounts list filtering.

import { useMemo } from 'react';
import { C, MONO } from '../lib/tokens';
import { CHIP_BASE, CHIP_ON } from '../lib/styles';
import { derive, type Derived, type Thresholds } from '../lib/mappers';
import type { AccountRow } from '../types/api';
import { useData, useServerNow, useUi } from '../store/hooks';
import { EMPTY_FILTERS, setFilter, toggleQuick, uiStore, type SelectFilters } from '../store/uiStore';

type QuickPredicate = (a: AccountRow, d: Derived, thr: Thresholds, recent: boolean) => boolean;

export const QUICK: Record<string, { label: (thr: Thresholds) => string; pred: QuickPredicate }> = {
  attn: { label: () => 'Needs attention', pred: (_a, d) => d.rank < 3 },
  crit: { label: () => 'Critical', pred: (_a, d) => d.risk === 'critical' },
  off: { label: () => 'Offline', pred: (_a, d) => d.conn === 'offline' },
  bdis: { label: () => 'Broker disc.', pred: (_a, d) => d.bc === 'disconnected' },
  pos: { label: () => 'Has positions', pred: (a) => a.positions_count > 0 },
  dd: { label: (thr) => 'DD > ' + thr.dW + '%', pred: (_a, d, thr) => d.ddNum != null && d.ddNum > thr.dW },
  lowm: { label: (thr) => 'Margin < ' + thr.mW + '%', pred: (_a, d, thr) => d.mlvlNum != null && d.mlvlNum < thr.mW },
  rec: { label: () => 'Changed 5m', pred: (_a, _d, _thr, recent) => recent },
};

export const QUICK_ORDER = ['attn', 'crit', 'off', 'bdis', 'pos', 'dd', 'lowm', 'rec'];

/** IDs of accounts changed within the last 5 minutes (from the flash map). */
export function useRecentIds(): Set<string> {
  const flash = useData((s) => s.flash);
  const nowLocal = Date.now();
  return useMemo(() => {
    const s = new Set<string>();
    for (const key in flash) if (nowLocal - flash[key]! < 300000) s.add(key.split(':')[0]!);
    return s;
  }, [flash, nowLocal]);
}

export function AccountFilters({ includeSavedViews = true }: { includeSavedViews?: boolean }) {
  const accounts = useData((s) => s.accounts);
  const thr = useUi((s) => s.thr);
  const quick = useUi((s) => s.quick);
  const f = useUi((s) => s.f);
  const views = useUi((s) => s.views);
  const viewSel = useUi((s) => s.viewSel);
  const saveOpen = useUi((s) => s.saveOpen);
  const serverNow = useServerNow();
  const recentIds = useRecentIds();

  const derivedMap = useMemo(() => {
    const m = new Map<string, Derived>();
    for (const a of accounts) m.set(a.id, derive(a, serverNow, thr));
    return m;
  }, [accounts, serverNow, thr]);

  const uniq = (fn: (a: AccountRow) => string) => Array.from(new Set(accounts.map(fn))).sort();
  const selDefs: Array<[keyof SelectFilters, string, string[]]> = [
    ['vps', 'VPS', uniq((a) => a.vps_id)],
    ['broker', 'Broker', uniq((a) => a.broker)],
    ['ccy', 'Currency', uniq((a) => a.currency)],
    ['risk', 'Risk', ['normal', 'warning', 'critical']],
    ['conn', 'Conn', ['live', 'delayed', 'stale', 'offline']],
    ['strat', 'Strategy', uniq((a) => a.strategy)],
  ];

  const presetViews: Array<[string, string]> = [
    ['', 'View: priority'],
    ['attn', 'Needs attention'],
    ['crit', 'Critical risk'],
    ['pos', 'Open positions'],
    ['lowm', 'Low margin'],
  ];

  function applyView(v: string) {
    const f0 = { ...EMPTY_FILTERS };
    if (!v) uiStore.set({ viewSel: '', q: '', quick: {}, f: f0, sort: { k: 'sev', d: 1 } });
    else if (v.startsWith('p:')) uiStore.set({ viewSel: v, q: '', f: f0, quick: { [v.slice(2)]: true }, sort: { k: 'sev', d: 1 } });
    else {
      const sv = views[Number(v.slice(2))];
      if (sv) uiStore.set({ viewSel: v, q: sv.snap.q, quick: sv.snap.quick, f: sv.snap.f, sort: sv.snap.sort });
    }
  }

  return (
    <>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {QUICK_ORDER.map((k) => {
          const active = !!quick[k];
          const n = accounts.filter((a) => QUICK[k]!.pred(a, derivedMap.get(a.id)!, thr, recentIds.has(a.id))).length;
          return (
            <button key={k} onClick={() => toggleQuick(k)} aria-pressed={active} style={active ? CHIP_ON : CHIP_BASE}>
              <span>{QUICK[k]!.label(thr)}</span>
              <span style={{ fontFamily: MONO, fontSize: 10, opacity: 0.75 }}>{n}</span>
            </button>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {selDefs.map(([id, label, vals]) => (
          <label key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: C.dm }}>
            {label}
            <select
              value={f[id]}
              onChange={(e) => setFilter(id, e.target.value)}
              style={{ background: '#121B2C', border: '1px solid ' + C.ln, color: C.tx, borderRadius: 5, height: 26, fontSize: 11.5, padding: '0 4px', maxWidth: 150 }}
            >
              <option value="">All</option>
              {vals.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </label>
        ))}
        {includeSavedViews && (
          <>
            <span style={{ width: 1, height: 18, background: C.ln }} aria-hidden="true" />
            <select
              value={viewSel}
              onChange={(e) => applyView(e.target.value)}
              aria-label="Saved views"
              style={{ background: '#121B2C', border: '1px solid ' + C.ln, color: C.tx, borderRadius: 5, height: 26, fontSize: 11.5, padding: '0 4px' }}
            >
              {presetViews.map((p) => (
                <option key={p[0]} value={p[0] ? 'p:' + p[0] : ''}>
                  {p[1]}
                </option>
              ))}
              {views.map((v, i) => (
                <option key={i} value={'s:' + i}>
                  {v.name}
                </option>
              ))}
            </select>
            {!saveOpen && (
              <button onClick={() => uiStore.set({ saveOpen: true })} style={{ background: 'none', border: 'none', color: C.bl, fontSize: 11.5, padding: 0 }}>
                Save view…
              </button>
            )}
          </>
        )}
      </div>
    </>
  );
}
